import Address from '../../models/Address';
import BaseBackendService from '../BaseBackendService';
import { CRUDResult } from './adminBackendServiceTypes';

import { adminBackendServiceActivityMixin } from './adminBackendServiceActivityMixin';
import { adminBackendServiceSearchMixin } from './adminBackendServiceSearchMixin';
import { adminBackendServiceFundOpportunityMixin } from './adminBackendServiceFundOpportunityMixin';
import { adminBackendServiceInvestorStatusMixin } from './adminBackendServiceInvestorStatusMixin';
import { adminBackendServiceContactMixin } from './adminBackendServiceContactMixin';
import { adminBackendServiceContactPortfolioMixin } from './adminBackendServiceContactPortfolioMixin';
import { adminBackendServiceInvestorMixin } from './adminBackendServiceInvestorMixin';
import { adminBackendServiceContentMixin } from './adminBackendServiceContentMixin';
import { adminBackendServiceArticleMixin } from './adminBackendServiceArticleMixin';
import { adminBackendServiceNewsMixin } from './adminBackendServiceNewsMixin';
import { adminBackendServiceDocumentCategoryMixin } from './adminBackendServiceDocumentCategoryMixin';
import { adminBackendServiceClientExceptionMixin } from './adminBackendServiceClientExceptionMixin';
import { adminBackendServiceLogCaptureMixin } from './adminBackendServiceLogCaptureMixin';
import { adminBackendServiceContentGenerationMixin } from './adminBackendServiceContentGenerationMixin';
import { adminBackendServiceContentExtractionMixin } from './adminBackendServiceContentExtractionMixin';
import { adminBackendServiceImageCardsMixin } from './adminBackendServiceImageCardsMixin';
import { adminBackendServiceVeraMixin } from './adminBackendServiceVeraMixin';
import { adminBackendServiceVeraAdminAssistMixin } from './adminBackendServiceVeraAdminAssistMixin';
import { adminBackendServiceEntityMixin } from './adminBackendServiceEntityMixin';
import { adminBackendServiceInvestorStatementMixin } from './adminBackendServiceInvestorStatementMixin';
import { adminBackendServiceAdminUserMixin } from './adminBackendServiceAdminUserMixin';
import { adminBackendServiceAdvisorMixin } from './adminBackendServiceAdvisorMixin';
import { adminBackendServiceReviewMixin } from './adminBackendServiceReviewMixin';
import { adminBackendServiceNotificationMixin } from './adminBackendServiceNotificationMixin';
import { adminBackendServiceOperationsMixin } from './adminBackendServiceOperationsMixin';
import { adminBackendServiceVideoLibraryMixin } from './adminBackendServiceVideoLibraryMixin';
import { adminBackendServiceDocumentLibraryMixin } from './adminBackendServiceDocumentLibraryMixin';
import type { AdminBackendServiceMixinMethods } from './adminBackendServiceInterface';

type AdminBackendServiceMixins = typeof adminBackendServiceActivityMixin &
  typeof adminBackendServiceSearchMixin &
  typeof adminBackendServiceFundOpportunityMixin &
  typeof adminBackendServiceInvestorStatusMixin &
  typeof adminBackendServiceContactMixin &
  typeof adminBackendServiceContactPortfolioMixin &
  typeof adminBackendServiceInvestorMixin &
  typeof adminBackendServiceContentMixin &
  typeof adminBackendServiceArticleMixin &
  typeof adminBackendServiceNewsMixin &
  typeof adminBackendServiceDocumentCategoryMixin &
  typeof adminBackendServiceClientExceptionMixin &
  typeof adminBackendServiceLogCaptureMixin &
  typeof adminBackendServiceContentGenerationMixin &
  typeof adminBackendServiceContentExtractionMixin &
  typeof adminBackendServiceImageCardsMixin &
  typeof adminBackendServiceVeraMixin &
  typeof adminBackendServiceVeraAdminAssistMixin &
  typeof adminBackendServiceEntityMixin &
  typeof adminBackendServiceInvestorStatementMixin &
  typeof adminBackendServiceAdminUserMixin &
  typeof adminBackendServiceAdvisorMixin &
  typeof adminBackendServiceReviewMixin &
  typeof adminBackendServiceNotificationMixin &
  typeof adminBackendServiceOperationsMixin &
  typeof adminBackendServiceVideoLibraryMixin &
  typeof adminBackendServiceDocumentLibraryMixin;

// Intentional interface/class declaration merging: the interface declares the
// mixin methods composed onto the prototype via Object.assign below, so callers
// see them as typed instance methods. This is the documented admin mixin pattern.
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface BackendService extends AdminBackendServiceMixinMethods {}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class BackendService extends BaseBackendService {
  static getAddresses(id: string) {
    const service = new BackendService();
    return service.authApiGetAuthed(
      `fundOpportunities/${id}/addresses`,
      'Unable to fetch addresses',
      (data: { data: any[] }) =>
        data.data.map(
          (a: any) =>
            ({
              ...a,
              latitude: parseFloat(a.latitude),
              longitude: parseFloat(a.longitude),
            }) as Address,
        ),
    );
  }

  static deleteAddress(addressId: string) {
    const service = new BackendService();
    return service.authApiMutateAuthed<CRUDResult>(
      `addresses/${addressId}`,
      'DELETE',
      undefined,
      (json) => json,
    );
  }

  static createAddress(address: Partial<Address>, fundOpportunityId: string) {
    const service = new BackendService();
    return service.authApiMutateAuthed<CRUDResult>(
      `fundOpportunities/${fundOpportunityId}/addresses`,
      'POST',
      address,
      (json) => json,
    );
  }
}

Object.assign(
  BackendService.prototype,
  adminBackendServiceActivityMixin,
  adminBackendServiceSearchMixin,
  adminBackendServiceFundOpportunityMixin,
  adminBackendServiceInvestorStatusMixin,
  adminBackendServiceContactMixin,
  adminBackendServiceContactPortfolioMixin,
  adminBackendServiceInvestorMixin,
  adminBackendServiceContentMixin,
  adminBackendServiceArticleMixin,
  adminBackendServiceNewsMixin,
  adminBackendServiceDocumentCategoryMixin,
  adminBackendServiceClientExceptionMixin,
  adminBackendServiceLogCaptureMixin,
  adminBackendServiceContentGenerationMixin,
  adminBackendServiceContentExtractionMixin,
  adminBackendServiceImageCardsMixin,
  adminBackendServiceVeraMixin,
  adminBackendServiceVeraAdminAssistMixin,
  adminBackendServiceEntityMixin,
  adminBackendServiceInvestorStatementMixin,
  adminBackendServiceAdminUserMixin,
  adminBackendServiceAdvisorMixin,
  adminBackendServiceReviewMixin,
  adminBackendServiceNotificationMixin,
  adminBackendServiceOperationsMixin,
  adminBackendServiceVideoLibraryMixin,
  adminBackendServiceDocumentLibraryMixin,
);

export default BackendService;

export type {
  CRUDResult,
  CloneMode,
  CloneFundOpportunityRequest,
  CloneFundOpportunityResponse,
  AdminUserUpdate,
  AdminNoteItem,
} from './adminBackendServiceTypes';
